/**
 * المرحلة 1.4 — one visit, loaded and written.
 *
 * Every write on the visit page goes through `write(fn, { ok, what })`: while
 * the visit is open it runs at once; once the visit is closed, the server
 * takes a change only with a reason (AMEND_REASON), so this asks for one
 * first and hands it to `fn(reason)`. The answer's `encounter` replaces the
 * page's copy, so every panel shows what the server now holds.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { encounterApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';
import { ReasonDialog } from './ReasonDialog';

/** Was this refusal the visit page's own switch, closed for the institution? */
export const switchClosed = (err, controlId = 'encounters') =>
  Boolean(err && err.status === 403 && err.body && err.body.control === controlId);

export function useEncounter(id, { initial = null } = {}) {
  const { success, error: toastError } = useNotifications();
  const [state, setState] = useState({ loading: !initial, error: null, encounter: initial });
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(null);
  const current = useRef(initial);

  const load = useCallback(
    (quiet = false) => {
      if (!id) return Promise.resolve(null);
      if (!quiet) setState((s) => ({ ...s, loading: true, error: null }));
      return encounterApi
        .get(id)
        .then((res) => {
          current.current = res.encounter;
          setState({ loading: false, error: null, encounter: res.encounter });
          return res.encounter;
        })
        .catch((error) => {
          setState((s) => ({ loading: false, error, encounter: s.encounter }));
          return null;
        });
    },
    [id],
  );

  useEffect(() => {
    if (!initial) load();
  }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Take an answer's `encounter` as the page's copy — the whole view only: a
   * certificate's or a referral's decision answers with a reference to its
   * visit (for the access log), which is not a page to draw.
   */
  const apply = useCallback((res) => {
    if (res && res.encounter && res.encounter.status && res.encounter.can) {
      current.current = res.encounter;
      setState((s) => ({ ...s, encounter: res.encounter }));
    }
    return res;
  }, []);

  /**
   * Run one write. Resolves the answer, or null when it was refused (the
   * message is toasted) or the reason was not given. `rethrow` hands the
   * error back instead, for a form that shows it beside its fields;
   * `noReason` is for a paper issued from a closed visit (a certificate, a
   * referral), which changes nothing the visit holds and needs no reason.
   */
  const write = useCallback(
    async (fn, { ok, what, rethrow = false, noReason = false } = {}) => {
      let reason;
      const enc = current.current;
      if (enc && enc.status !== 'open' && !noReason) {
        reason = await new Promise((resolve) => setAsking({ resolve, what }));
        setAsking(null);
        if (reason === null) return null;
      }
      setBusy(true);
      try {
        const res = await fn(reason);
        apply(res);
        if (ok) success(typeof ok === 'function' ? ok(res) : ok);
        return res;
      } catch (err) {
        if (rethrow) throw err;
        toastError(err.message);
        return null;
      } finally {
        setBusy(false);
      }
    },
    [apply, success, toastError],
  );

  const reasonDialog = (
    <ReasonDialog
      open={Boolean(asking)}
      what={asking ? asking.what : null}
      onCancel={() => asking && asking.resolve(null)}
      onConfirm={(text) => asking && asking.resolve(text)}
    />
  );

  return { ...state, busy, reload: load, apply, write, reasonDialog };
}

export default useEncounter;
